import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Download, FileText, KeySquare, MoreVertical, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WA = "https://wa.me/5542999587464";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [reports, setReports] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    Promise.allSettled([api.get("/me/licenses"), api.get("/me/reports")]).then(([l, r]) => {
      if (l.status === "fulfilled") setLicenses(l.value.data);
      if (r.status === "fulfilled") setReports(r.value.data);
      if ([l, r].some((x) => x.status === "rejected" && x.reason?.response?.status !== 401))
        toast.error("Erro ao carregar sua conta.");
    });
  }, []);

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen grain">
      <header className="border-b border-white/10 bg-ink/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <img src="/logo-full.png" alt="JeaxLabs" className="h-7 w-auto" />
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-white/50 hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="account-logout"
                    className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/15 hover:bg-white hover:text-ink transition-colors">
              <LogOut className="w-4 h-4" /> Sair
            </button>
            <button onClick={() => setMenuOpen((open) => !open)} className="sm:hidden p-2" aria-label="Abrir menu" data-testid="account-menu">
              {menuOpen ? <X className="w-5 h-5" /> : <MoreVertical className="w-5 h-5" />}
            </button>
            {menuOpen && <button onClick={doLogout} data-testid="account-logout-mobile"
              className="sm:hidden absolute right-5 top-14 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-3 bg-ink border border-white/15">
              <LogOut className="w-4 h-4" /> Sair
            </button>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-10 py-10">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">Minha conta</span>
        <h1 className="font-head font-black text-3xl md:text-5xl tracking-tighter mt-3 mb-10">Olá, {user?.name?.split(" ")[0] || "cliente"}</h1>

        <div className="flex items-center gap-3 mb-6">
          <KeySquare className="w-5 h-5 text-acid" />
          <h2 className="font-head font-bold text-xl md:text-2xl">Licenças possuídas</h2>
        </div>
        {licenses.length === 0 ? (
          <div className="border border-white/10 p-10 text-center text-white/40 font-mono text-sm mb-14" data-testid="no-licenses">
            Você ainda não possui licenças. Fale com a equipe para adquirir.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 mb-14" data-testid="licenses-grid">
            {licenses.map((l) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                          className="border border-white/10 bg-surface/40 p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-head font-bold text-xl">{l.name}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-acid/50 text-acid">Ativa</span>
                </div>
                <p className="text-white/50 text-sm mb-6">{l.tagline}</p>
                <a href={WA} target="_blank" rel="noreferrer" data-testid={`download-${l.id}`}
                   className="mt-auto inline-flex items-center justify-center gap-2 bg-cobalt text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors">
                  <Download className="w-4 h-4" /> Baixar instalador
                </a>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-5 h-5 text-cobalt" />
          <h2 className="font-head font-bold text-xl md:text-2xl">Relatórios</h2>
        </div>
        {reports.length === 0 ? (
          <div className="border border-white/10 p-10 text-center text-white/40 font-mono text-sm" data-testid="no-reports">
            Nenhum relatório disponível ainda.
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="reports-list">
            {reports.map((r) => (
              <div key={r.id} className="grid md:grid-cols-4 gap-2 md:gap-4 px-6 py-4 text-sm">
                <span className="font-medium">{r.title}</span>
                <span className="text-white/50">{r.period}</span>
                <span className="text-cobalt">R$ {r.revenue}</span>
                <span className="text-white/40">{r.notes}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
